import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { PageHeader } from '../components/PageHeader';
import { PageLayout } from '../components/PageLayout';
import { apiService } from '../services/api';
import type { TenantCustomField } from '../types';
import toast from 'react-hot-toast';

const PAGE_SIZE = 10;

export function CustomFieldsPage() {
  const navigate = useNavigate();
  const [fields, setFields] = useState<TenantCustomField[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const load = () => {
    setLoading(true);
    apiService.getCustomFields().then(setFields).catch(() => setFields([])).finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const handleDelete = async (id: string, label: string) => {
    if (!confirm(`Excluir o campo "${label}"? Os valores já preenchidos nos contatos não serão apagados, mas o campo não aparecerá mais.`)) return;
    try {
      await apiService.deleteCustomField(id);
      toast.success('Campo excluído');
      load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Erro ao excluir');
    }
  };

  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const searchLower = search.trim().toLowerCase();
  const filteredFields = searchLower
    ? fields.filter((f) => f.label.toLowerCase().includes(searchLower) || f.key.toLowerCase().includes(searchLower))
    : fields;
  const totalFiltered = filteredFields.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / PAGE_SIZE));
  const pageToShow = Math.min(currentPage, totalPages) || 1;
  const paginatedFields = filteredFields.slice((pageToShow - 1) * PAGE_SIZE, pageToShow * PAGE_SIZE);

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.setData('text/plain', String(index));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex !== null) setDragOverIndex(index);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    const related = e.relatedTarget as Node | null;
    if (!related || !(e.currentTarget as HTMLElement).contains(related)) {
      setDragOverIndex(null);
    }
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDrop = async (e: React.DragEvent, toIndex: number) => {
    e.preventDefault();
    setDraggedIndex(null);
    setDragOverIndex(null);
    const fromIndex = parseInt(e.dataTransfer.getData('text/plain'), 10);
    if (Number.isNaN(fromIndex) || fromIndex === toIndex) return;
    const reordered = [...fields];
    const [removed] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, removed);
    try {
      await apiService.reorderCustomFields(reordered.map((f) => f.id));
      toast.success('Ordem atualizada');
      setFields(reordered);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao reordenar');
    }
  };

  const typeLabel = (type: string) => (type === 'text' ? 'Texto' : type === 'number' ? 'Número' : 'Data');

  return (
    <>
      <Header title="Campos personalizados" subtitle={`${fields.length} campo(s) definido(s)`} compactTopbar />
      <PageLayout>
        <PageHeader
          title="Campos personalizados"
          breadcrumbs={[
            { label: 'Home', href: '/' },
            { label: 'Contatos', href: '/contatos' },
            { label: 'Campos personalizados' },
          ]}
        />
        <div className="space-y-8 w-full">
          <div className="card-padrao overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-4">
              <h2 className="font-semibold text-gray-800 dark:text-white/90">Campos personalizados</h2>
              {fields.length > 0 && (
                <div className="flex flex-wrap items-center gap-3">
                  <div className="relative w-48 sm:w-56">
                    <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 pointer-events-none" aria-hidden />
                    <input
                      type="text"
                      placeholder="Buscar por nome ou chave..."
                      value={search}
                      onChange={(e) => {
                        setSearch(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => navigate('/contatos/campos-personalizados/novo')}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-transparent text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 transition-colors"
                  >
                    <i className="fas fa-plus text-xl shrink-0" aria-hidden />
                    Adicionar campo
                  </button>
                </div>
              )}
            </div>
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500" />
                <span className="ml-2 text-gray-600 dark:text-gray-400">Carregando...</span>
              </div>
            ) : fields.length === 0 ? (
              <div className="p-12 text-center">
                <div className="text-6xl mb-4 text-gray-300 dark:text-gray-600" aria-hidden>
                  <i className="fas fa-sliders" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  Nenhum campo personalizado
                </h3>
                <p className="text-gray-500 dark:text-gray-400 mb-6">
                  Clique em Adicionar campo para criar seus primeiros campos (ex: Loja, Plano, Data de aniversário).
                </p>
                <button
                  type="button"
                  onClick={() => navigate('/contatos/campos-personalizados/novo')}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-transparent text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 transition-colors"
                >
                  <i className="fas fa-plus text-base" aria-hidden />
                  Adicionar campo
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/contatos')}
                  className="ml-3 inline-flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm font-medium"
                >
                  <i className="fas fa-arrow-left text-base shrink-0" aria-hidden />
                  Voltar para Contatos
                </button>
              </div>
            ) : totalFiltered === 0 ? (
              <div className="p-6 text-center text-gray-500 dark:text-gray-400">
                Nenhum campo encontrado para &quot;{search}&quot;.
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-800/50">
                      <tr>
                        <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 w-10" aria-label="Arrastar para reordenar" />
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Nome</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Chave</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Tipo</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Obrigatório</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Ações</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-white/[0.02] divide-y divide-gray-200 dark:divide-gray-700">
                      {paginatedFields.map((f) => {
                        const actualIndex = fields.findIndex((ff) => ff.id === f.id);
                        const isDragging = draggedIndex === actualIndex;
                        const isDropTarget = dragOverIndex === actualIndex;
                        return (
                          <tr
                            key={f.id}
                            onDragOver={handleDragOver}
                            onDragEnter={(e) => handleDragEnter(e, actualIndex)}
                            onDragLeave={handleDragLeave}
                            onDrop={(e) => handleDrop(e, actualIndex)}
                            className={`
                              hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-all duration-150
                              ${isDragging ? 'opacity-40 scale-[0.98] bg-gray-100 dark:bg-gray-800/50' : ''}
                              ${isDropTarget && !isDragging ? 'ring-2 ring-inset ring-emerald-500 dark:ring-emerald-400 bg-emerald-50/70 dark:bg-emerald-900/20' : ''}
                            `}
                          >
                            <td className="px-2 py-4 whitespace-nowrap w-10">
                              <div
                                draggable
                                onDragStart={(e) => handleDragStart(e, actualIndex)}
                                onDragEnd={handleDragEnd}
                                className="cursor-grab active:cursor-grabbing p-2 -m-2 rounded text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50 touch-none"
                                role="button"
                                tabIndex={0}
                                aria-label="Arrastar para reordenar"
                                onKeyDown={(e) => {
                                  if (e.key === ' ' || e.key === 'Enter') e.preventDefault();
                                }}
                              >
                                <i className="fas fa-grip-vertical" aria-hidden />
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white/90">{f.label}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 font-mono">{f.key}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{typeLabel(f.type)}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{f.required ? 'Sim' : 'Não'}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <div className="flex gap-2 justify-end">
                                <button
                                  type="button"
                                  onClick={() => navigate(`/contatos/campos-personalizados/${f.id}`)}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 font-medium transition-colors"
                                  title="Editar campo"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                  </svg>
                                  Editar
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDelete(f.id, f.label)}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 font-medium transition-colors"
                                  title="Excluir campo"
                                >
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                  Excluir
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {totalPages > 1 && (
                  <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-between gap-3">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Mostrando {(pageToShow - 1) * PAGE_SIZE + 1}–{Math.min(pageToShow * PAGE_SIZE, totalFiltered)} de {totalFiltered} campo(s)
                    </p>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                        disabled={pageToShow <= 1}
                        className="px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Anterior
                      </button>
                      <span className="text-sm text-gray-600 dark:text-gray-300">
                        Página {pageToShow} de {totalPages}
                      </span>
                      <button
                        type="button"
                        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                        disabled={pageToShow >= totalPages}
                        className="px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Próxima
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
          {fields.length > 0 && (
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => navigate('/contatos')}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800/50 hover:bg-gray-50 dark:hover:bg-gray-800 focus:ring-2 focus:ring-offset-2 focus:ring-gray-300 dark:focus:ring-gray-600 transition-colors"
              >
                <i className="fas fa-arrow-left text-base shrink-0" aria-hidden />
                Voltar para Contatos
              </button>
            </div>
          )}
        </div>
      </PageLayout>
    </>
  );
}
